/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  isObjectOrArrayAndEmpty,
  excludeCurrentMonth,
  getFirstDayofSelectedMonth,
  formattedDate,
  getEndDateForUtility
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import IUtilityCostComponentProps from './IUtilityCostComponentProps';
import UtilityComponent from '../UtilityComponent/UtilityComponent';
import useMounted from '../../../shared/useMount/useMount';
import {
  ISiteCard,
  IUtilityBillElect,
  IUtilityBillElectData,
  IUtilityCostData,
  IUtilityPercentProps
} from '../../IEnergyManagementProps';
import moment from 'moment';
import NoData from '../../../shared/ErrorComponents/NoData';
import { UtilityCostType } from '../../../shared/Utils/Constants';
import { EmaasSubscriptionType } from '../../../shared/Enums';

interface IUtilityCost extends ISiteCard {
  data: IUtilityCostData;
  isPrevPeriodForAllMonths?: boolean;
}

const UtilityCostComponentContent = (props: IUtilityCost) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, isPrevPeriodForAllMonths } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <UtilityComponent
      data={data}
      isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
    />
  );
};

const UtilityCostComponentWithLoader = withErrorStatus(
  withLoadingStatus(UtilityCostComponentContent)
);

const UtilityCostComponent = (props: IUtilityCostComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext, widgetType } = props;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime =
    requestContext.context.subscriptionType ===
    EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
      ? getEndDateForUtility(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        )
      : excludeCurrentMonth(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        );

  const isMounted = useMounted();

  const endTimeNewFormat = formattedDate(endTime);

  const connectorConfig = {
    name: 'utility-cost',
    options: {
      startDate: startTime,
      endDate: endTimeNewFormat
    }
  };
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const isDateRangeValid = () => {
    const sDate = moment(
      getFirstDayofSelectedMonth(requestContext.context.startTime!),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      excludeCurrentMonth(
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  const { all, loading, error, status } = dataConnector.data;
  const UtilityCost = (all as unknown) as IUtilityCostData;
  const isDataAvailable = UtilityCost ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityCostData);
  const [isPrevPeriodForAllMonths, setIsPrevPeriodForAllMonths] = useState<
    boolean
  >(true);

  const checkIfAllSiteHasCompleteData = (data: IUtilityCostData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllMonthHasCompleteData = (data: IUtilityCostData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: IUtilityCostData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: {
          previousPeriodCost: number | null;
          totalActualCost: number | null;
        }) =>
          item.previousPeriodCost !== 0 &&
          item.previousPeriodCost !== null &&
          item.totalActualCost !== 0 &&
          item.totalActualCost !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: IUtilityCostData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: IUtilityPercentProps) =>
          item.aggregations.previousPeriodCost !== 0 &&
          item.aggregations.previousPeriodCost !== null &&
          item.aggregations.totalActualCost !== 0 &&
          item.aggregations.totalActualCost !== null
      )
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        if (widgetType === UtilityCostType.portfolio) {
          setIsPrevPeriodForAllMonths(
            checkIfAllMonthsHasPreviousPeriod(UtilityCost) &&
              checkIfAllSiteHasCompleteData(UtilityCost) &&
              checkIfAllMonthHasCompleteData(UtilityCost) &&
              checkIfAllSitesHasPreviousPeriod(UtilityCost)
          );
        }

        setData(UtilityCost);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <UtilityCostComponentWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={status}
      onRefresh={onRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
    />
  );
};
export default UtilityCostComponent;
